/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file calendarReminders.js
 * @description
 * Phone-side reminder engine for calendar events — schedules LOCAL, alarm-style
 * lead-time notifications for the events the user participates in.
 *
 * DIVISION OF LABOR (critical — prevents double alerts): the BACKEND cron
 * already delivers every `style: 'notification'` reminder as a push to every
 * device. This module therefore locally schedules ONLY `style: 'alarm'`
 * reminders — the loud, alarm-like alerts that must fire even when the device
 * is offline. A reminder row is never handled by both sides.
 *
 * Design:
 * - Module-level engine (not a hook): callable from screens, contexts and
 *   background tasks alike.
 * - DETERMINISTIC identifiers `calrem:<eventId>:<minutesBefore>` make
 *   {@link syncAlarmReminders} idempotent per call — each run reconciles the
 *   pending schedule against the wanted set (cancel stale, keep matching,
 *   schedule missing) without persisting anything beyond the scheduled
 *   notifications themselves.
 * - HARD CAP {@link MAX_SCHEDULED_ALARMS} (~40) soonest fire times: iOS
 *   silently drops local notifications beyond 64 pending per app, so the cap
 *   leaves headroom for the mail/system banners the app also schedules.
 * - Tap deep-link: the shared notification responder
 *   (`mailNotifications.setupMailNotificationResponder` →
 *   `notificationNavigation.resolveNotificationNavigationTarget`) resolves the
 *   `data.screen` key exactly like mail pushes do, so a tapped alarm opens the
 *   Management home (calendar tab host).
 */

import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import i18n from '../../i18n';

/** Android notification channel dedicated to loud calendar alarms. */
export const ALARM_CHANNEL_ID = 'calendar-alarm';

/** Identifier prefix of every locally scheduled calendar reminder. */
export const REMINDER_ID_PREFIX = 'calrem:';

/**
 * Most pending alarm reminders kept scheduled at once. iOS silently drops
 * local notifications beyond 64 pending per app; ~40 leaves headroom for the
 * mail / sync banners this app also schedules.
 */
export const MAX_SCHEDULED_ALARMS = 40;

/** Ignore fire times closer than this — they would fire mid-scheduling. */
const MIN_LEAD_MS = 5 * 1000;

/**
 * Deterministic identifier of one event's one alarm reminder.
 *
 * @param {string} eventId - Calendar event id.
 * @param {number} minutesBefore - Lead time in minutes.
 * @returns {string} `calrem:<eventId>:<minutesBefore>`
 */
export const buildReminderIdentifier = (eventId, minutesBefore) => `${REMINDER_ID_PREFIX}${eventId}:${minutesBefore}`;

/** Whether the expo-notifications native module is usable on this platform. */
const notificationsAvailable = () => Platform.OS !== 'web'
    && Boolean(Notifications)
    && typeof Notifications.scheduleNotificationAsync === 'function'
    && typeof Notifications.getAllScheduledNotificationsAsync === 'function';

// Serialize concurrent channel creators (bootstrap + sync can race), mirroring
// notificationChannels.ensureNotificationChannels.
let alarmChannelPromise = null;

/**
 * Create/refresh the dedicated Android `calendar-alarm` channel: importance
 * MAX (heads-up + sound), audible default sound, strong vibration, PUBLIC on
 * the lock screen, and NO DND bypass (an event reminder must respect Do Not
 * Disturb). Safe to call repeatedly — Android upserts by id and updates the
 * localized name in place, so calling it before every sync keeps the channel
 * name in the user's current app language (same pattern as
 * `notificationChannels.ensureNotificationChannels`). No-op on iOS/web.
 *
 * @returns {Promise<void>}
 */
export const ensureAlarmChannel = async () => {
    if (Platform.OS !== 'android') {
        return;
    }
    if (typeof Notifications?.setNotificationChannelAsync !== 'function') {
        return;
    }
    if (alarmChannelPromise) {
        return alarmChannelPromise;
    }

    alarmChannelPromise = (async () => {
        try {
            await Notifications.setNotificationChannelAsync(ALARM_CHANNEL_ID, {
                name: i18n.t('calendar_reminder_channel_name', { defaultValue: 'Naptár ébresztők' }),
                importance: Notifications.AndroidImportance.MAX,
                sound: 'default',
                vibrationPattern: [0, 500, 250, 500, 250, 500],
                lightColor: '#f1b34b',
                lockscreenVisibility: Notifications.AndroidNotificationVisibility.PUBLIC,
                bypassDnd: false,
            });
        } catch (_e) {
            // Best-effort: a failed channel upsert must never block scheduling —
            // the notification then lands on the manifest fallback channel.
        } finally {
            alarmChannelPromise = null;
        }
    })();

    return alarmChannelPromise;
};

/**
 * Localized notification body for one lead time ("X perc múlva kezdődik"
 * style), rendered AT SCHEDULE TIME in the app's current language.
 *
 * @param {number} minutesBefore - Lead time in minutes (0 = at start).
 * @returns {string}
 */
const buildReminderBody = (minutesBefore) => {
    if (!minutesBefore) {
        return i18n.t('calendar_reminder_body_now', { defaultValue: 'Most kezdődik' });
    }
    if (minutesBefore % 1440 === 0) {
        return i18n.t('calendar_reminder_body_days', {
            defaultValue: '{{count}} nap múlva kezdődik',
            count: minutesBefore / 1440,
        });
    }
    if (minutesBefore % 60 === 0) {
        return i18n.t('calendar_reminder_body_hours', {
            defaultValue: '{{count}} óra múlva kezdődik',
            count: minutesBefore / 60,
        });
    }

    return i18n.t('calendar_reminder_body_minutes', {
        defaultValue: '{{count}} perc múlva kezdődik',
        count: minutesBefore,
    });
};

/**
 * Compute the wanted future alarm firings for one normalized event.
 *
 * @param {Object} event - Normalized event ({ id | _id, title, start, notifications }).
 * @param {number} now - Reference epoch ms.
 * @returns {Array<{identifier: string, eventId: string, title: string, minutesBefore: number, fireAt: number}>}
 */
const wantedFiringsForEvent = (event, now) => {
    if (!event || typeof event !== 'object') {
        return [];
    }
    const eventId = String(event.id || event._id || '').trim();
    const startMs = event.start ? new Date(event.start).getTime() : NaN;
    const notifications = event.notifications || null;

    if (!eventId || !Number.isFinite(startMs) || !notifications || notifications.enabled === false) {
        return [];
    }

    const reminders = Array.isArray(notifications.reminders) ? notifications.reminders : [];

    return reminders
        .filter((reminder) => reminder && reminder.style === 'alarm')
        .map((reminder) => {
            const minutesBefore = Math.max(0, Math.floor(Number(reminder.minutesBefore)) || 0);

            return {
                identifier: buildReminderIdentifier(eventId, minutesBefore),
                eventId,
                title: String(event.title || '').trim(),
                minutesBefore,
                fireAt: startMs - minutesBefore * 60 * 1000,
            };
        })
        .filter((firing) => firing.fireAt > now + MIN_LEAD_MS);
};

/**
 * Schedule one alarm firing as a local notification on the dedicated channel.
 *
 * @param {{identifier: string, eventId: string, title: string, minutesBefore: number, fireAt: number}} firing
 * @returns {Promise<void>}
 */
const scheduleFiring = async (firing) => {
    await Notifications.scheduleNotificationAsync({
        identifier: firing.identifier,
        content: {
            title: firing.title || i18n.t('calendar_reminder_default_title', { defaultValue: 'Naptáresemény' }),
            body: buildReminderBody(firing.minutesBefore),
            sound: 'default',
            // iOS: break through Focus / silent modes — an event alarm is
            // exactly what Time Sensitive is for (honoured best-effort).
            ...(Platform.OS === 'ios' ? {
                interruptionLevel: 'timeSensitive',
                threadIdentifier: 'calendar',
            } : {}),
            // Strings only — expo-notifications data must stay JSON-primitive.
            // `screen`/`clickAction` deep-link through the shared responder the
            // same way mail pushes do; `fireAt` lets the next sync detect a
            // rescheduled event behind an unchanged identifier.
            data: {
                type: 'calendar',
                eventKey: 'calendar.event',
                eventId: firing.eventId,
                minutesBefore: String(firing.minutesBefore),
                style: 'alarm',
                fireAt: String(firing.fireAt),
                screen: 'ManagementHomeScreen',
                clickAction: '/calendar',
            },
        },
        trigger: {
            type: Notifications.SchedulableTriggerInputTypes?.DATE || 'date',
            date: new Date(firing.fireAt),
            ...(Platform.OS === 'android' ? { channelId: ALARM_CHANNEL_ID } : {}),
        },
    });
};

/**
 * Reconcile the pending local schedule with the given events' ALARM-STYLE
 * reminders (`notifications.reminders[]` rows with `style: 'alarm'` only —
 * `style: 'notification'` rows are the backend cron's job, see the file
 * header). Idempotent per call and self-contained:
 *
 * 1. Compute every future fire time (`start - minutesBefore`, past times
 *    skipped) and keep the {@link MAX_SCHEDULED_ALARMS} soonest.
 * 2. Cancel every previously scheduled `calrem:*` notification that is no
 *    longer wanted — including a still-wanted identifier whose stored
 *    `data.fireAt` no longer matches (the event was rescheduled).
 * 3. Schedule the wanted firings that are not already pending.
 *
 * Nothing is persisted beyond the scheduled notifications themselves.
 * Never throws — scheduling is best-effort by design.
 *
 * @param {Array<Object>} events - Normalized events ({ id | _id, title, start,
 *   notifications }) — e.g. the calendar list or the widgets-summary rows.
 * @returns {Promise<{scheduled: number, cancelled: number, kept: number}>} reconciliation counts (zeros on failure).
 */
export const syncAlarmReminders = async (events) => {
    const result = { scheduled: 0, cancelled: 0, kept: 0 };

    if (!notificationsAvailable()) {
        return result;
    }

    try {
        await ensureAlarmChannel();

        const now = Date.now();
        const list = Array.isArray(events) ? events : [];
        const wanted = new Map();

        list.forEach((event) => {
            wantedFiringsForEvent(event, now).forEach((firing) => {
                wanted.set(firing.identifier, firing);
            });
        });

        // Keep only the soonest firings under the iOS pending-local cap.
        const capped = new Map(
            Array.from(wanted.values())
                .sort((a, b) => a.fireAt - b.fireAt)
                .slice(0, MAX_SCHEDULED_ALARMS)
                .map((firing) => [firing.identifier, firing])
        );

        const pending = await Notifications.getAllScheduledNotificationsAsync();
        const pendingIds = new Set();

        // 1. Cancel stale / rescheduled `calrem:*` entries.
        for (const scheduled of (Array.isArray(pending) ? pending : [])) {
            const identifier = String(scheduled?.identifier || '');

            if (!identifier.startsWith(REMINDER_ID_PREFIX)) {
                continue;
            }
            const want = capped.get(identifier);
            const storedFireAt = String(scheduled?.content?.data?.fireAt || '');
            const upToDate = Boolean(want) && (!storedFireAt || storedFireAt === String(want.fireAt));

            if (upToDate) {
                pendingIds.add(identifier);
                result.kept += 1;
                continue;
            }
            try {
                // eslint-disable-next-line no-await-in-loop
                await Notifications.cancelScheduledNotificationAsync(identifier);
                result.cancelled += 1;
            } catch (_e) {
                // A single failed cancel must never abort the reconcile.
            }
        }

        // 2. Schedule the missing firings.
        for (const firing of capped.values()) {
            if (pendingIds.has(firing.identifier)) {
                continue;
            }
            try {
                // eslint-disable-next-line no-await-in-loop
                await scheduleFiring(firing);
                result.scheduled += 1;
            } catch (_e) {
                // One bad row must never abort the batch.
            }
        }
    } catch (_e) {
        // Best-effort engine: a failed sync self-heals on the next call.
    }

    return result;
};

/**
 * Cancel every locally scheduled alarm reminder of one event (event deleted,
 * or its notifications were turned off).
 *
 * @param {string} eventId - Calendar event id.
 * @returns {Promise<number>} number of cancelled notifications (0 on failure).
 */
export const cancelEventReminders = async (eventId) => {
    const id = String(eventId || '').trim();

    if (!id || !notificationsAvailable()) {
        return 0;
    }

    let cancelled = 0;

    try {
        const pending = await Notifications.getAllScheduledNotificationsAsync();
        const prefix = `${REMINDER_ID_PREFIX}${id}:`;

        for (const scheduled of (Array.isArray(pending) ? pending : [])) {
            const identifier = String(scheduled?.identifier || '');

            if (!identifier.startsWith(prefix)) {
                continue;
            }
            try {
                // eslint-disable-next-line no-await-in-loop
                await Notifications.cancelScheduledNotificationAsync(identifier);
                cancelled += 1;
            } catch (_e) {
                // Best-effort per id.
            }
        }
    } catch (_e) {
        // Best-effort: leftovers are pruned by the next syncAlarmReminders run.
    }

    return cancelled;
};

export default {
    ALARM_CHANNEL_ID,
    REMINDER_ID_PREFIX,
    MAX_SCHEDULED_ALARMS,
    buildReminderIdentifier,
    ensureAlarmChannel,
    syncAlarmReminders,
    cancelEventReminders,
};
